import { useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { EmailTokenInfo, SessionUser } from '@jupi/shared'
import { ApiError, api, useAuth } from '@jupi/ui'
import { AuthCard } from '@jupi/ui'
import { rememberMethod, takeFragmentToken } from '@jupi/ui'

// Where a magic sign-in email lands: /login/email#token=... The member
// presses a button to sign in rather than the page doing it on load, so a
// mail provider's link scanner, which opens every link it sees, cannot
// spend the link before its owner gets to it.
export default function EmailLinkPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { acceptSession } = useAuth()
  const [token] = useState(takeFragmentToken)
  const [info, setInfo] = useState<EmailTokenInfo | null>(null)
  const [invalid, setInvalid] = useState(!token)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return
    api
      .post<EmailTokenInfo>('/api/auth/email-token/inspect', { token })
      .then((found) => (found.purpose === 'magic_link' ? setInfo(found) : setInvalid(true)))
      .catch(() => setInvalid(true))
  }, [token])

  const signIn = async () => {
    if (!token) return
    setBusy(true)
    setError(null)
    try {
      const { user } = await api.post<{ user: SessionUser }>('/api/auth/magic-link/verify', { token })
      rememberMethod('magic_link')
      acceptSession(user)
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError(t('auth.accountInactive'))
      else setInvalid(true)
    } finally {
      setBusy(false)
    }
  }

  if (invalid) {
    return (
      <AuthCard title={t('auth.magicTitle')} subtitle={t('app.intra')}>
        <Stack spacing={2}>
          <Alert severity="warning">{t('auth.linkInvalid')}</Alert>
          <Button component={RouterLink} to="/login" variant="contained" fullWidth>
            {t('auth.backToSignIn')}
          </Button>
        </Stack>
      </AuthCard>
    )
  }

  if (!info) {
    return (
      <AuthCard title={t('auth.magicTitle')} subtitle={t('auth.checkingLink')}>
        <Stack sx={{ alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={t('auth.magicTitle')} subtitle={t('app.intra')}>
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Typography>{t('auth.magicIntro', { name: info.displayName, email: info.email })}</Typography>
        <Button variant="contained" size="large" fullWidth disabled={busy} onClick={signIn} autoFocus>
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {t('auth.magicWhy')}
        </Typography>
      </Stack>
    </AuthCard>
  )
}
